import { useEffect } from 'react'
import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'
import { Motion } from '@/lib/motion/lazy-motion'
import Tooltip from '@/components/ui/Tooltip'
import ScrollView from '@/components/ui/ScrollView'
import Indicator from '@/components/search/common/Indicator'
import ContextToggle from '@/components/search/common/ContextToggle'
import EmptyResults from '@/components/search/core/EmptyResults'
import { Icon } from '@/icons/Icon'
import { useChatContext } from '@/context/ChatContext'
import { cn } from '@/lib/utils'

type RichInputProps = {
  condensed?: boolean
  visible?: boolean
  hideIndicator?: boolean
}

export default function RichInput({
  condensed = false,
  visible = true,
  hideIndicator = false,
}: RichInputProps) {
  const {
    isTraffic,
    inputRef,
    command,
    caret,
    context,
    collection,
    handleInput,
    handleKeyDown,
    results,
    insertCommand,
    selectedIndex,
    setSelectedIndex,
    showSearch,
    closeSearch,
    searching,
  } = useChatContext()

  useEffect(() => {
    if (!condensed || !inputRef.current) return

    const scrollToEnd = () => {
      const scrollContainer = inputRef.current?.closest('.overflow-x-auto') as HTMLElement
      if (!scrollContainer) return

      // Always scroll to the end to show the latest content
      scrollContainer.scrollTo({
        left: scrollContainer.scrollWidth,
        behavior: 'smooth',
      })
    }

    // Watch for any DOM changes in the input (typing, command insertion, etc.)
    const observer = new MutationObserver(() => {
      scrollToEnd()
    })

    observer.observe(inputRef.current, {
      childList: true,
      subtree: true,
      characterData: true,
    })

    // Also scroll on initial mount and when condensed changes
    scrollToEnd()

    return () => observer.disconnect()
  }, [condensed])

  const placeholder = condensed
    ? isTraffic
      ? 'Search apps'
      : 'Search or ask AI'
    : context === 'chat'
      ? 'Ask Efficient AI anything. Type @ for app mentions and / for shortcuts'
      : 'Search anything. Type @ for apps and / for shortcuts'

  const iconClass = `flex h-8 items-center justify-center shrink-0 ${condensed ? 'w-auto' : 'w-8'}`

  function getCommandTitle(): string {
    if (collection === 'deal' || collection === 'review' || collection === 'stack')
      return `${collection}s`
    if (command?.textContent?.trim().startsWith('@')) return 'Apps'
    if (command?.textContent?.trim().startsWith('#')) return 'Categories'
    if (command?.textContent?.trim().startsWith('/')) return 'Shortcuts'
    return 'Search'
  }

  return (
    <div
      className={cn(
        'relative z-40 flex min-w-0 flex-1',
        condensed ? 'items-center gap-2' : 'gap-3 p-2',
      )}
    >
      {condensed && visible && !isTraffic ? (
        <ContextToggle condensed={condensed} />
      ) : (
        <div className={iconClass}>
          <Icon
            name={
              condensed ? 'magnifier-sparkle' : context === 'chat' ? 'chat-sparkle' : 'magnifier'
            }
            size={condensed ? 14 : context === 'search' ? 20 : 18}
          />
        </div>
      )}

      <div
        className={cn(
          'relative flex w-full min-w-0',
          condensed
            ? `overflow-x-hidden ${visible ? 'text-16' : 'text-16 md:text-13'}`
            : 'pr-5 pt-[3px] text-16',
        )}
      >
        <ScrollView>
          <div
            ref={inputRef}
            contentEditable
            suppressContentEditableWarning
            suppressHydrationWarning
            className={cn(
              'w-full max-w-full cursor-text leading-[1.7] empty:before:block empty:before:h-full empty:before:pt-px empty:before:leading-normal empty:before:text-gray-400 empty:before:content-[attr(data-placeholder)] focus:outline-none dark:empty:before:text-white/50',
              condensed ? 'whitespace-nowrap' : 'max-h-16 overflow-y-auto',
            )}
            data-placeholder={placeholder}
            data-chat-input="true"
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onBlur={(e) => {
              const element = e.currentTarget
              if (!element.textContent?.trim().length) {
                element.textContent = ''
              }
            }}
            data-dashlane-ignore="true"
            data-1p-ignore="true"
            data-lpignore="true"
            data-form-type="other"
            data-bwignore="true"
            role="combobox"
            aria-label="Search and command palette"
            aria-autocomplete="list"
            aria-expanded={Boolean(showSearch || (command && caret))}
          ></div>
        </ScrollView>
      </div>

      <AnimatePresence mode="wait">
        {searching && condensed ? (
          <Icon name="loader" className="absolute right-0 top-[7px] animate-spin" />
        ) : showSearch && condensed ? (
          <div onClick={() => closeSearch(true)} className={iconClass} key="esc-indicator">
            <Indicator label="Esc" />
          </div>
        ) : (
          <Tooltip
            node={
              <p className="[&>span]:rounded-md [&>span]:bg-primary-500/20 [&>span]:px-1 [&>span]:py-px [&>span]:text-primary-500">
                Pro tip: type <span>/</span> to use shortcuts. Use shortcuts like{' '}
                <span>/review</span>, <span>/compare</span>, or <span>/best</span> to access content
                in 1-click.
              </p>
            }
            width={280}
            disabled={condensed}
            className="hidden navigation:block"
          >
            {!hideIndicator && (
              <div className={iconClass} key="f-indicator">
                <Indicator label="/" />
              </div>
            )}
          </Tooltip>
        )}
      </AnimatePresence>

      <Motion>
        <AnimatePresence>
          {command && caret && !condensed && !searching && (
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute z-10 w-fit min-w-64 overflow-hidden rounded-xl border border-gray-200 bg-white text-13 font-medium shadow-xl"
              style={{
                top: caret?.y ?? 0,
                left: caret?.x ?? 0,
              }}
            >
              <ScrollView className="h-fit max-h-[285px] px-2 pb-2 pt-1" direction="vertical">
                <span className="flex h-11 shrink-0 items-center px-1.5 text-14 capitalize text-gray-950 md:h-9 md:text-12">
                  {getCommandTitle()}
                </span>
                {results.length === 0 ? (
                  <EmptyResults />
                ) : (
                  results.map((result, index) => (
                    <div
                      key={`${result.type}-${index}`}
                      onMouseEnter={() => setSelectedIndex(index)}
                      onClick={() => insertCommand(result)}
                      className={cn(
                        'group relative flex h-11 shrink-0 cursor-pointer items-center gap-10 rounded-lg pl-[9px] pr-3 text-gray-500 transition-colors md:h-9',
                        selectedIndex === index && 'bg-primary-50 text-primary-500',
                      )}
                    >
                      <div className="flex items-center gap-3.5 whitespace-nowrap text-15 md:text-13">
                        <div
                          className={cn(
                            'flex h-5 w-5 items-center justify-center text-gray-500 transition-colors',
                            selectedIndex === index && 'text-primary-500',
                          )}
                        >
                          {result.icon}
                        </div>
                        {result.label}
                      </div>

                      {collection && collection !== 'search' && collection !== 'comparison' && (
                        <div
                          className={cn(
                            'ml-auto flex translate-x-2.5 items-center gap-2 truncate text-14 text-primary-500 opacity-0 transition-all md:text-13',
                            selectedIndex === index && 'translate-x-0 opacity-100',
                          )}
                        >
                          Go to {collection}
                          <Icon name="arrow-right-small" size={12} />
                        </div>
                      )}
                    </div>
                  ))
                )}
              </ScrollView>
            </m.div>
          )}
        </AnimatePresence>
      </Motion>
    </div>
  )
}
